import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  computed,
  effect,
  signal,
  untracked,
} from '@angular/core';
import moment from 'moment';
import { ActivatedRoute, NavigationEnd, NavigationStart, ParamMap, Router } from '@angular/router';
import { Modal } from 'flowbite';
import { Subscription } from 'rxjs';
import { AppComponent } from 'src/app/app.component';
import {
  IActiveTicket,
  IActiveTicketGroup,
  IBase,
  IBooking,
  IPlaybackRange,
  IPositionsDayRange,
  ITicketDrawerRequest,
  IServiceInfo,
  ITown,
  ITrip,
  ITripDetail,
  ITripStop,
  ITripsRefreshEvent,
  ITripWithPositions,
  IVehicle,
} from 'src/app/models';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import {
  Colors,
  ExpeditionStatus,
  ExpeditionType,
  ExportableListType,
  NotificationTypes,
  ServiceType,
  TripsViewMode,
} from 'src/app/models/enums';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { ITripPosition } from 'src/app/models/trip/tripPosition';
import { TripsResolver } from 'src/app/resolvers';
import {
  DEFAULT_TRIP_STATUS,
  TRIP_FILTER_KEYS,
  deserializeTripsFilters,
  hasAnyFilterKey,
  parseId,
  parseTripsFiltersFromStorage,
  resolvePositionsDayRange,
} from 'src/app/filters';
import {
  BookingsService,
  MapUtilsService,
  NotificationService,
  StorageService,
  TownsService,
  TripsService,
  UsersService,
  UtilsService,
  ListExportService,
} from 'src/app/services';
import { ApiRouterService } from 'src/app/services/http/api.router';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { initialTripsState } from 'src/app/store/initialisation/trips';
import { environment } from 'src/environments/environment';
import { images } from 'src/images';
import { APP } from 'src/app/config/app';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import {
  buildNewTicketFromTrip,
  groupActiveTicketsByCategory,
  suggestionUserTypeOf,
} from 'src/app/utils/suggestionTickets';

@Component({
  selector: 'app-trips',
  templateUrl: './trips.component.html',
  styleUrls: ['./trips.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TripsComponent implements OnInit, AfterViewInit, OnDestroy {
  public trips = initialTripsState;
  public controller = new AbortController();
  public images = images;
  public map: google.maps.Map;
  public dataLayer: google.maps.Data;
  public selectedTrip: ITrip | undefined;
  public tripDetails: ITripDetail | undefined;
  public stops: any[] = [];
  public townStops: any[] = [];
  public townMarkers: HTMLElement[] = [];
  public bounds: google.maps.LatLngBounds;
  public towns: ITown[] = [];
  public selectedTowns: ITown[] = [];

  public bookings: IBookingDetails[] = [];

  public clientsSelected: IBase[] = [];
  public operatorsSelected: IBase[] = [];
  public servicesSelected: IBase[] = [];
  public routesSelected: IBase[] = [];
  public driversSelected: IBase[] = [];
  public vehiclesSelected: IBase[] = [];
  //public townsSelected: IBase[];
  public typesSelected: ExpeditionType[] = [];
  public serviceTypesSelected: ServiceType[] = [];
  public statusSelected: ExpeditionStatus[] = [];
  public fromSelected?: Date;
  public toSelected?: Date;
  public availabilityIdSelected: number | null = null;
  public itineraryIdSelected: number | null = null;
  public searchValue: string;

  public changeDriver: Modal;
  public changeDriverShowed: boolean = false;
  public driverSelected?: number;

  public changeVehicle: Modal;
  public changeVehicleShowed: boolean = false;
  public vehicleSelected?: number;

  public editComment: Modal;
  public editCommentShowed: boolean = false;
  public commentText?: string;

  public addPax: Modal;
  public addPaxShowed: boolean = false;
  public nPax?: number;

  public setReady: Modal;
  public setReadyShowed: boolean = false;

  public setClosed: Modal;
  public setClosedShowed: boolean = false;

  public deleteTrip: Modal;
  public deleteTripShowed: boolean = false;
  public deletingTrip = false;
  public reason: string;

  public deleteBooking: Modal;
  public deleteBookingShowed: boolean = false;
  public deletingBooking = false;
  public bookingToDelete: number;
  public comment: string;

  public viewBookings: Modal;
  public viewBookingsShowed: boolean = false;
  public showDate = false;
  private routerSubscription: Subscription;

  public tripSelected: ITrip | undefined;
  public path: any;

  public getTripInterval: any;
  public getVehiclesInterval: any;
  public getBookingsInterval: any;
  public getDrivenRouteInterval: any;
  private positionsRefreshInterval?: ReturnType<typeof setInterval>;

  public searching = false;

  public viewMode = TripsViewMode.TRIPS;
  public readonly TripsViewMode = TripsViewMode;
  private readonly positionsControlsSpacer = this.createPositionsControlsSpacer();
  public positionsDayRange?: IPositionsDayRange;
  private positionsTownsKey?: string;
  readonly positionsDay = signal<Date | undefined>(undefined);
  readonly positionsTripRange = signal<IPlaybackRange | undefined>(undefined, {
    equal: (previous, current) =>
      previous?.startMinute === current?.startMinute && previous?.endMinute === current?.endMinute,
  });
  private readonly positionsMinute = signal<number | null>(null);
  private readonly positionsTripId = signal<number | null>(null);
  private readonly mapVersion = signal(0);
  private readonly positionsVehicles = computed(() => {
    const minute = this.positionsMinute();
    const day = this.positionsDay();
    if (minute === null || !day) return [];
    return this.tripsService.vehiclePositionsAt(
      this.tripsResolver.tripsWithPositions(),
      day,
      minute,
      this.positionsTripId(),
    );
  });
  readonly tripsWithoutMarker = computed(() => {
    const withoutMarker: Record<number, boolean> = {};
    if (this.positionsMinute() === null) return withoutMarker;
    const drawnTripIds = new Set(this.positionsVehicles().map((vehicle) => vehicle.tripId));
    for (const trip of this.tripsResolver.tripsWithPositions()) {
      if (!drawnTripIds.has(trip.id!)) withoutMarker[trip.id!] = true;
    }
    return withoutMarker;
  });
  readonly positionsDelays = computed(() => {
    const delays: Record<number, number> = {};
    for (const vehicle of this.positionsVehicles()) {
      if (vehicle.delay != null) delays[vehicle.tripId] = vehicle.delay;
    }
    return delays;
  });

  public serviceInfos: IServiceInfo[] = [];

  public selectedTripActiveTicketGroups: IActiveTicketGroup[] = [];
  private activeTicketsTripId?: number;
  public ticketDrawerRequest?: ITicketDrawerRequest;

  public navigationSubscription: Subscription;
  private queryParamsSub?: Subscription;

  constructor(
    public tripsService: TripsService,
    public routerService: ApiRouterService,
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public mapUtils: MapUtilsService,
    public usersService: UsersService,
    public bookingsService: BookingsService,
    public notificationService: NotificationService,
    @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef,
    private storageService: StorageService,
    public tripsResolver: TripsResolver,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public appComponent: AppComponent,
    private townsService: TownsService,
    public mapUtilsService: MapUtilsService,
    private suggestionService: SuggestionService,
    private listExportService: ListExportService,
  ) {
    effect(() => {
      this.mapVersion();
      const vehicles = this.positionsVehicles();
      if (this.positionsMinute() === null || !this.map) {
        this.mapUtilsService.clearVehiclePositions();
        return;
      }
      this.mapUtilsService.renderVehiclePositions(this.map, vehicles, (tripId) => this.getTrip(tripId, true));
    });
    effect(() => {
      this.mapVersion();
      const tripsWithPositions = this.tripsResolver.tripsWithPositions();
      untracked(() => this.zoomToPositionsTowns(tripsWithPositions));
    });
  }

  async ngOnInit() {
    this.applyPositionsDayRange(this.activatedRoute.snapshot.queryParamMap);
    const town = await this.usersService.getLoggedUserTown();
    const coords = {
      lat: town.latitude ? town.latitude : 41.4696546,
      lng: town.longitude ? town.longitude : 2.0596017,
    };

    // Selected towns: URL is canonical, storage holds full objects (with lat/lng) for enrichment.
    const areaJson = await this.storageService.getArea();
    const cachedTowns: ITown[] = areaJson ? JSON.parse(areaJson) : [];
    const urlTowns = this.activatedRoute.snapshot.queryParamMap.get('towns');
    if (urlTowns) {
      const ids = urlTowns
        .split(',')
        .map((s) => Number(s))
        .filter((n) => !isNaN(n));
      this.selectedTowns = ids
        .map((id) => cachedTowns.find((t) => t.id === id))
        .filter((t): t is ITown => t != null);
    } else if (cachedTowns.length > 0) {
      this.selectedTowns = cachedTowns;
    }

    if (this.selectedTowns.length > 0) {
      if (this.selectedTowns.length == 1) {
        this.setCenter({
          lat: this.selectedTowns[0].latitude,
          lng: this.selectedTowns[0].longitude,
        });
        this.drawServiceInfos();
      } else {
        this.setCenter(coords).then(() => this.selectedTownsZoom());
      }
    } else {
      this.setCenter(coords);
    }

    this.setVehiclesInterval();

    this.mapUtils.setUserCurrentPosition(this.map, this.images.user.name);

    this.literalService.setTitle('sidebar.trips', ' - NEMI Backoffice');

    // Hydration from storage happens in tripsHydrationGuard before the resolver
    // fires, so there's exactly one API call per sidebar entry.

    // Handle child route parameters on init
    this.applyViewModeFromRoute(false);
    this.handleChildRoute();

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        // Close the modal when navigating away from the current view
        this.closeModals();
      }
    });

    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        // Handle navigation between trips child routes without full reload
        if (e.urlAfterRedirects?.startsWith('/trips')) {
          this.applyViewModeFromRoute(true);
          this.handleChildRoute();
          return;
        }
        this.clearMap();
        this.ngOnDestroy();
        this.reload();
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.initFilters();
    document.addEventListener('visibilitychange', () => {
      if (
        document.visibilityState === 'visible' &&
        this.dataLayer &&
        this.map
      ) {
        this.dataLayer.setMap(null);
        this.dataLayer.setMap(this.map);
      }
    });

    await this.usersService.getPossibleClientTowns().then((res) => {
      const sortedTowns = res.towns.sort(
        (firstItem: IBase, secondItem: IBase) =>
          firstItem.name!.localeCompare(secondItem.name!, undefined, {
            sensitivity: 'base',
          }),
      );
      this.towns = sortedTowns;
    });

    // React to URL changes (back/forward, programmatic nav, sidebar re-entry). Navigating
    // between /trips URLs keeps this component alive, so this is the only hook that runs.
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.applyPositionsDayRange(params);
      this.applyTownsFromUrl(params.get('towns') ?? '');
      this.applyAvailabilityFromUrl(params);
    });
  }

  private applyAvailabilityFromUrl(params: ParamMap): void {
    this.availabilityIdSelected = parseId(params.get('availability'));
    this.itineraryIdSelected = parseId(params.get('itinerary'));
    this.updateTitle();
  }

  private applyTownsFromUrl(urlTownsRaw: string): void {
    const currentKey = this.selectedTowns
      .map((t) => t.id)
      .sort()
      .join(',');
    const incomingIds = urlTownsRaw
      ? urlTownsRaw
          .split(',')
          .map(Number)
          .filter((n) => !isNaN(n))
      : [];
    const incomingKey = [...incomingIds].sort().join(',');
    if (currentKey === incomingKey) return;

    this.selectedTowns = incomingIds
      .map((id) => this.towns.find((t) => t.id === id))
      .filter((t): t is ITown => t != null);
    // Re-render the map regions for the new selection.
    this.selectedTownsZoom();
    this.mapUtilsService.clearMarkers();
    this.clearTownMap();
    this.serviceInfos = [];
    if (this.selectedTowns.length === 1) {
      this.drawServiceInfos();
    }
    clearInterval(this.getVehiclesInterval);
    this.setVehiclesInterval();
  }

  reload() {
    this.selectedTrip = undefined;
  }

  handleChildRoute() {
    const childRoute = this.activatedRoute.firstChild;
    // No child route -> URL is /trips, close any open trip detail so the list view shows.
    if (!childRoute) {
      if (this.selectedTrip || this.tripDetails) {
        this.clearMap();
      }
      return;
    }

    const tripId: number = childRoute.snapshot.params['id']
      ? +childRoute.snapshot.params['id']
      : 0;
    if (!tripId) {
      if (this.selectedTrip || this.tripDetails) {
        this.clearMap();
      }
      return;
    }

    const isBookingsOnly = childRoute.snapshot.data['bookingsOnly']; // /trips/bookings/:id
    const isBookingsFromDetail =
      childRoute.snapshot.url.some((segment) => segment.path === 'bookings') &&
      !isBookingsOnly; // /trips/:id/bookings

    if (isBookingsOnly) {
      this.showViewBookings(tripId);
    } else if (isBookingsFromDetail) {
      this.getTrip(tripId);
      this.showViewBookings(tripId);
    } else {
      this.getTrip(tripId);
    }
  }

  async selectedTownsZoom(): Promise<void> {
    if (this.selectedTowns.length > 0) {
      this.zoomToTowns(this.selectedTowns);
    } /*else {
      // TODO I wpuld say this should only be done at the beggining. It goes slow and it seems buggy when using the app
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position: GeolocationPosition) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }
            this.map.setCenter(pos);
          }
        )
      }
    }*/
  }

  async setCenter(coords: any) {
    const { Map } = (await google.maps.importLibrary(
      'maps',
    )) as google.maps.MapsLibrary;
    this.map = new Map(document.getElementById('map') as HTMLElement, {
      center: coords,
      zoom: APP.DEFAULT_MAP_ZOOM,
      mapId: environment.mapId,
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      fullscreenControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
    });
    this.map.addListener('click', () => {
      this.mapUtilsService.clearInfoWindows();
    });
    this.updatePositionsControlsSpacer();
    this.mapUtilsService.clearVehiclePositions();
    this.positionsTownsKey = undefined;
    this.mapVersion.update((version) => version + 1);
  }

  trackByFn(index: number): any {
    return index;
  }

  getColor(stop: ITripStop): string {
    const hasIn = stop.passengersIn > 0 || stop.prmIn > 0;
    const hasOut = stop.passengersOut > 0 || stop.prmOut > 0;
    if (hasIn && hasOut) return '#a0673a';
    if (hasIn) return '#06cb60';
    if (hasOut) return '#ed8936';
    if (stop.realTime) return '#848ea8';
    return this.selectedTrip && this.selectedTrip.service && this.selectedTrip.service.color || Colors.TRANSPARENT;
  }

  async refreshTrips(event: ITripsRefreshEvent) {
    this.searching = true;
    this.activatedRoute.snapshot = event.snapshot;
    this.activatedRoute.snapshot.data['trips'] = this.trips;
    this.clientsSelected = (event && event.clientsSelected) ?? [];
    this.operatorsSelected = (event && event.operatorsSelected) ?? [];
    this.servicesSelected = (event && event.servicesSelected) ?? [];
    this.routesSelected = (event && event.routesSelected) ?? [];
    this.driversSelected = (event && event.driversSelected) ?? [];
    this.vehiclesSelected = (event && event.vehiclesSelected) ?? [];
    this.typesSelected = (event && event.typesSelected) ?? [];
    this.serviceTypesSelected = event?.serviceTypesSelected ?? [];
    this.statusSelected = (event && event.statusSelected) ?? [];
    this.availabilityIdSelected = (event && event.availabilityIdSelected) ?? null;
    this.itineraryIdSelected = (event && event.itineraryIdSelected) ?? null;
    this.searchValue = (event && event.searchValue) ?? '';
    this.fromSelected = event?.from;
    this.toSelected = event?.to;
    await this.getTrips();
    this.searching = false;
  }

  async getTrips() {
    await this.tripsResolver.getTrips(
      this.activatedRoute.snapshot,
      this.clientsSelected,
      this.operatorsSelected,
      this.selectedTowns,
      this.typesSelected,
      this.statusSelected,
      this.searchValue,
      this.servicesSelected,
      this.routesSelected,
      this.fromSelected,
      this.toSelected,
      this.availabilityIdSelected,
      this.driversSelected,
      this.vehiclesSelected,
      this.serviceTypesSelected,
      this.itineraryIdSelected
    );
  }

  setVehiclesInterval() {
    clearInterval(this.getVehiclesInterval);
    if (this.selectedTrip || this.viewMode === TripsViewMode.POSITIONS) return;
    const townIds = this.selectedTowns.map((town) => town.id!);
    this.getTownsRealTimePositions(townIds);
    this.getVehiclesInterval = setInterval(() => {
      const townIds = this.selectedTowns.map((town) => town.id!);
      this.getTownsRealTimePositions(townIds);
    }, 10000);
  }

  async getTownsRealTimePositions(townsIds: number[]) {
    if (townsIds.length <= 0) return;
    const mapUtils = this.mapUtils;
    const map = this.map;
    await this.tripsService
      .getTownsPositions(townsIds)
      .then((townPositions: ITripPosition[]) => {
        townPositions.map((bus: any) => {
          mapUtils.addMarker(
            map,
            bus,
            images.bus,
            false,
            100,
            true,
            (tripId: number) =>
              this.selectedTrip?.id !== tripId && this.getTripDetails(tripId),
          );
        });
      });
  }

  async getTripRealTimePositions(tripId: number) {
    if (!tripId) return;
    const mapUtils = this.mapUtils;
    const map = this.map;
    await this.tripsService
      .getTripPosition(tripId)
      .then((tripPosition: ITripPosition) => {
        tripPosition &&
          mapUtils.addMarker(map, tripPosition, images.bus, false, 100, true);
      });
  }

  async getTripDetails(tripId: number) {
    if (this.activeTicketsTripId !== tripId) this.loadSelectedTripActiveTickets(tripId);
    try {
      await this.tripsService
      .getTrip(tripId)
      .then((tripRecieved: ITripDetail) => {
        this.selectedTrip = tripRecieved;
        this.tripSelected = tripRecieved;
        if (this.viewMode === TripsViewMode.POSITIONS) {
          this.positionsDay.set(moment(tripRecieved.dateTime).startOf('day').toDate());
          this.positionsTripRange.set(this.tripsService.tripPlaybackRange(tripRecieved));
        }
        const index = this.trips.value.findIndex((trip: ITrip) => trip.id === tripRecieved.id);
        if (index && index != -1) this.trips.value[index] = tripRecieved;
        if (this.redrawTrip(tripRecieved)) {
          this.tripDetails = tripRecieved;
          this.changeDetectorRef.detectChanges();
          if (this.dataLayer) this.dataLayer.forEach((feature: any) => this.dataLayer.remove(feature));
          else {
            this.dataLayer = new google.maps.Data();
            this.dataLayer.setMap(this.map);
          }
          this.dataLayer.setStyle({
            strokeColor: this.selectedTrip && this.selectedTrip.service && this.selectedTrip.service.color || Colors.TRANSPARENT,
            strokeWeight: 5,
          });
          this.dataLayer.addGeoJson(tripRecieved.path);
          this.updateSegmentsColors(tripRecieved);

          this.path = tripRecieved.path;
          const bounds = new google.maps.LatLngBounds();
          this.path.features.forEach((feature: any) => {
            const featureCoordinates = feature.geometry?.type === 'Point'
              ? [feature.geometry.coordinates]
              : feature.geometry?.coordinates ?? [];
            featureCoordinates.forEach((coordinate: any) => {
              bounds.extend(new google.maps.LatLng(coordinate[1], coordinate[0]));
            });
          });
          const padding = { top: 40, bottom: 30, left: 700, right: 10 };

          setTimeout(() => {
            this.map.fitBounds(bounds, padding);
          }, 500);
          this.addStopMarkers();
        } else {
          this.tripDetails = tripRecieved;
          this.updateSegmentsColors(tripRecieved);
          this.changeDetectorRef.detectChanges();
        }
        if (this.viewMode !== TripsViewMode.POSITIONS) this.getTripRealTimePositions(tripId);
      });
    } catch {
      this.router.navigate([this.basePath]);
    }
  }

  redrawTrip(tripRecieved: ITripDetail) {
    if (!this.tripDetails) return true;
    if (this.tripDetails.stops.length != tripRecieved.stops.length) return true;
    if (
      this.tripDetails.stops.some(
        (item1, index) => item1.id != tripRecieved.stops[index].id,
      )
    )
      return true;
    return false;
  }

  updateSegmentsColors(tripRecieved: ITripDetail) {
    this.dataLayer.forEach((feature: any) => {
      feature.toGeoJson((ft: any) => {
        const startSegmentStopId = ft.properties.start.properties.id;
        const startSegmentStop = tripRecieved.stops.find(
          (stop) => stop.id === startSegmentStopId,
        );
        const endSegmentStopId = ft.properties.end.properties.id;
        const endSegmentStop = tripRecieved.stops.find(
          (stop) => stop.id === endSegmentStopId,
        );
        if (startSegmentStop?.realTime && endSegmentStop?.realTime) {
          this.dataLayer.overrideStyle(feature, {
            strokeColor: '#848ea8',
            zIndex: 10,
          });
        }
      });
    });
  }

  async getTrip(tripId: number, updateUrl: boolean = false) {
    this.selectedTrip = this.trips.value.find(
      (trip: ITrip) => tripId === trip.id,
    )!;
    this.getTripDetails(tripId);
    this.startTripPolling(tripId);
    this.positionsTripId.set(tripId);
    clearInterval(this.getVehiclesInterval);
    this.showTownMarkers(false);
    if (updateUrl) {
      // Preserve current query params (filters, page, search) so back-button returns to the
      // same filtered list and shared URLs reconstruct the same view.
      const query = new URLSearchParams(
        Object.entries(this.activatedRoute.snapshot.queryParams)
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const url = query ? `${this.basePath}/${tripId}?${query}` : `${this.basePath}/${tripId}`;
      history.pushState(null, '', url);
    }
  }

  addStopMarkers(clear: boolean = false) {
    this.stops = [];
    if (!clear) {
      this.tripDetails!.stops.map((stop: any, index: number) => {
        // Keyed by position, not stop id: a stop visited twice needs its own marker for each visit
        let marker = document.getElementById(
          'stopMarker' + index,
        ) as HTMLElement;
        let zIndex = this.getZIndex(stop, index);
        if (index === 0) {
          marker = document.getElementById('origin') as HTMLElement;
        } else if (index === this.tripDetails!.stops.length - 1) {
          marker = document.getElementById('destination') as HTMLElement;
          const start = this.tripDetails!.stops[0];
          if (
            start.latitude === stop.latitude &&
            start.longitude === stop.longitude
          ) {
            const markerElement = marker.firstChild as HTMLElement;
            markerElement.style.paddingLeft = '8px';
          }
        }
        this.stops.push({
          id: stop.id,
          name: stop.name,
          element: marker,
          latitude: stop.latitude,
          longitude: stop.longitude,
          radius: stop.radius,
          color: this.getColor(stop),
          zIndex,
          start: index === 0,
          end: index === this.tripDetails!.stops.length - 1,
        });
      });
    }
    this.mapUtils.addCustomMarkers(this.map, this.stops, clear, undefined, true);
  }

  getZIndex(stop: ITripStop, index: number): number {
    if (index === 0) return 50;
    if (index === this.tripDetails!.stops.length - 1) return 40;
    return this.hasPassengerActivity(stop) ? 30 : 20;
  }

  private hasPassengerActivity(stop: ITripStop): boolean {
    return stop.passengersIn > 0 || stop.prmIn > 0 || stop.passengersOut > 0 || stop.prmOut > 0;
  }

  async drawServiceInfos() {
    this.serviceInfos = await this.townsService.getServices(
      this.selectedTowns[0].id!,
    );
    this.mapUtilsService.drawOutlines(this.map, this.serviceInfos);
    this.bounds = new google.maps.LatLngBounds();
    this.selectedTowns.forEach((town: ITown) => {
      this.bounds.union(
        new google.maps.LatLngBounds(
          new google.maps.LatLng(town.latitude, town.longitude),
        ),
      );
    });
    const padding = { top: 30, bottom: 20, left: 500, right: 0 };
    this.map.fitBounds(this.bounds, padding);
    this.map.setZoom(14);
  }

  showTownMarkers(show: boolean) {
    if (this.townMarkers.length > 0) {
      this.mapUtilsService.showClusterMarkers(show);
    }
  }

  clearTownMap() {
    this.townMarkers = [];
    this.mapUtilsService.clearOutlines();
    this.mapUtilsService.clearClustersMarkers();
  }

  clearMap() {
    this.addStopMarkers(true);
    this.map.data.forEach((feature: any) => this.map.data.remove(feature));
    this.dataLayer.forEach((feature: any) => this.dataLayer.remove(feature));
    clearInterval(this.getTripInterval);
    this.positionsTripId.set(null);
    this.positionsTripRange.set(undefined);
    this.selectedTrip = undefined;
    this.tripDetails = undefined;
    this.selectedTripActiveTicketGroups = [];
    this.activeTicketsTripId = undefined;
    this.setVehiclesInterval();
    this.showTownMarkers(true);
    this.selectedTownsZoom();
  }

  async loadSelectedTripActiveTickets(tripId: number) {
    this.activeTicketsTripId = tripId;
    const activeTicketsByExpedition = await this.suggestionService
      .getActiveTickets([tripId])
      .catch(() => ({}) as Record<number, IActiveTicket[]>);
    if (this.activeTicketsTripId === tripId)
      this.selectedTripActiveTicketGroups = groupActiveTicketsByCategory(activeTicketsByExpedition[tripId] ?? []);
  }

  openNewTicket(trip: ITrip) {
    this.ticketDrawerRequest = buildNewTicketFromTrip(trip);
  }

  openActiveTicket(ticket: IActiveTicket) {
    this.ticketDrawerRequest = {
      userType: suggestionUserTypeOf(ticket.type),
      suggestionId: ticket.id,
    };
  }

  closeTicketDrawer() {
    this.ticketDrawerRequest = undefined;
  }

  onTicketSaved() {
    if (this.activeTicketsTripId) this.loadSelectedTripActiveTickets(this.activeTicketsTripId);
    this.tripsResolver.loadActiveTickets(this.trips);
  }

  tripAlterationUrl(): string | null {
    const alteration = this.tripDetails?.alteration;
    if (!alteration?.serviceInfoId || !alteration.routeId) return null;
    return `/services/${alteration.serviceInfoId}/routes/${alteration.routeId}/alterations?alteration=${alteration.id}`;
  }

  async initFilters() {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const filters = hasAnyFilterKey(params, TRIP_FILTER_KEYS)
      ? deserializeTripsFilters(params)
      : parseTripsFiltersFromStorage(
          await this.storageService.getTripsFilters(),
        );
    if (!filters) return;
    this.clientsSelected = filters.selectedClients;
    this.operatorsSelected = filters.selectedOperators;
    this.servicesSelected = filters.selectedServices || [];
    this.routesSelected = filters.selectedRoutes || [];
    this.driversSelected = filters.selectedDrivers || [];
    this.vehiclesSelected = filters.selectedVehicles || [];
    this.statusSelected = filters.selectedStatus?.length
      ? filters.selectedStatus
      : DEFAULT_TRIP_STATUS;
    this.typesSelected = filters.selectedTypes;
    this.serviceTypesSelected = filters.selectedServiceTypes ?? [];
    this.availabilityIdSelected = filters.selectedAvailabilityId;
    this.itineraryIdSelected = filters.selectedItineraryId;
  }

  get basePath(): string {
    return APP.TRIPS_BASE_PATHS[this.viewMode];
  }

  changeViewMode(mode: TripsViewMode): void {
    this.router.navigate([APP.TRIPS_BASE_PATHS[mode]], {
      queryParams: { page: null },
      queryParamsHandling: 'merge',
    });
  }

  private applyViewModeFromRoute(refetch: boolean): void {
    const viewMode = this.activatedRoute.firstChild?.snapshot.data['viewMode'] ?? TripsViewMode.TRIPS;
    if (viewMode === this.viewMode) return;
    this.viewMode = viewMode;
    this.updateTitle();
    this.updatePositionsControlsSpacer();
    if (refetch) this.tripsResolver.resolve(this.activatedRoute.snapshot);
    if (this.viewMode === TripsViewMode.POSITIONS) {
      this.startPositionsPolling();
    } else {
      this.startTripsPolling();
    }
  }

  private zoomToTowns(towns: ITown[]): void {
    const padding = { top: 30, bottom: 30, left: 550, right: 0 };
    const bounds = new google.maps.LatLngBounds();
    towns.forEach((town: ITown) => {
      if (town.latitude && town.longitude) {
        bounds.union(new google.maps.LatLngBounds(new google.maps.LatLng(town.latitude, town.longitude)));
      }
    });
    if (bounds.isEmpty()) return;
    this.map.fitBounds(bounds, padding);
    this.map.setZoom(Math.min(this.map.getZoom()!, 15));
  }

  private zoomToPositionsTowns(trips: ITripWithPositions[]): void {
    if (!this.map || !trips.length || this.selectedTrip) return;
    const townsById = new Map(trips.filter((trip) => trip.town).map((trip) => [trip.town.id, trip.town]));
    const townsKey = [...townsById.keys()].sort().join(',');
    if (townsKey === this.positionsTownsKey) return;
    this.positionsTownsKey = townsKey;
    this.zoomToTowns([...townsById.values()]);
  }

  onPositionsTimeChange(time: Date): void {
    this.positionsMinute.set(time.getHours() * APP.MINUTES_PER_HOUR + time.getMinutes());
  }

  onPositionsDayChange(day: Date): void {
    this.positionsDay.set(day);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { [APP.POSITIONS_DAY_PARAM]: moment(day).format(APP.POSITIONS_DAY_FORMAT), page: null },
      queryParamsHandling: 'merge',
    });
  }

  private applyPositionsDayRange(params: ParamMap): void {
    this.positionsDayRange = resolvePositionsDayRange(params);
    this.positionsDay.set(this.positionsDayRange.day);
  }

  private startTripPolling(tripId: number): void {
    clearInterval(this.getTripInterval);
    if (this.viewMode === TripsViewMode.POSITIONS) return;
    this.getTripInterval = setInterval(() => {
      this.getTripDetails(tripId);
    }, 10000);
  }

  private startPositionsPolling(): void {
    clearInterval(this.getTripInterval);
    clearInterval(this.getVehiclesInterval);
    this.mapUtilsService.clearRealtimeMarkers();
    clearInterval(this.positionsRefreshInterval);
    this.positionsRefreshInterval = setInterval(
      () => this.refreshOngoingPositions(),
      APP.POSITIONS_REFRESH_INTERVAL_MS,
    );
  }

  private refreshOngoingPositions(): void {
    const day = this.positionsDay();
    if (!day || !moment(day).isSame(moment(), 'day')) return;
    const hasOngoingTrip = this.tripsResolver
      .tripsWithPositions()
      .some((trip) => trip.status === ExpeditionStatus.ONGOING);
    if (hasOngoingTrip) this.tripsResolver.refreshCurrentPage(this.activatedRoute.snapshot);
  }

  private startTripsPolling(): void {
    clearInterval(this.positionsRefreshInterval);
    this.positionsTownsKey = undefined;
    this.positionsMinute.set(null);
    this.setVehiclesInterval();
    if (this.selectedTrip?.id) this.startTripPolling(this.selectedTrip.id);
  }

  private createPositionsControlsSpacer(): HTMLElement {
    const spacer = Object.assign(document.createElement('div'), { index: -1 });
    spacer.style.height = `${APP.POSITIONS_MAP_CONTROLS_OFFSET_PX}px`;
    return spacer;
  }

  private updatePositionsControlsSpacer(): void {
    if (!this.map) return;
    const controls = this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM];
    const index = controls.getArray().indexOf(this.positionsControlsSpacer);
    const isPositions = this.viewMode === TripsViewMode.POSITIONS;
    if (isPositions && index === -1) controls.push(this.positionsControlsSpacer);
    if (!isPositions && index !== -1) controls.removeAt(index);
  }

  private updateTitle(): void {
    if (this.viewMode === TripsViewMode.POSITIONS) {
      this.storageService.setTitleKey('trips.positions.name');
      return;
    }
    if (this.itineraryIdSelected) {
      this.storageService.setTitleKey('trips.titleByItinerary');
      return;
    }
    this.storageService.setTitleKey(this.availabilityIdSelected ? 'trips.titleByAvailability' : null);
  }

  closeModals() {
    if (this.changeDriverShowed) this.changeDriver.toggle();
    if (this.changeVehicleShowed) this.changeVehicle.toggle();
    if (this.editCommentShowed) this.editComment.toggle();
    if (this.addPaxShowed) this.addPax.toggle();
    if (this.setReadyShowed) this.setReady.toggle();
    if (this.setClosedShowed) this.setClosed.toggle();
    if (this.deleteTripShowed) this.deleteTrip.toggle();
    if (this.viewBookingsShowed) this.viewBookings.toggle();
    if (this.deleteBookingShowed) this.deleteBooking.toggle();
  }

  async changeDriverAction(id: any) {
    const index = this.trips.value.findIndex(
      (trip: ITrip) => trip.id === this.tripSelected!.id,
    );
    const tripDetail = await this.tripsService.changeDriver(
      this.tripSelected!.id!,
      id,
    );
    this.trips.value[index] = tripDetail;
    if (this.selectedTrip) {
      this.selectedTrip = tripDetail;
      this.tripDetails = tripDetail;
    }
    this.changeDriver.hide();
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.changeDriver',
      NotificationTypes.SUCCESS,
    );
  }

  async changeVehicleAction(id: any) {
    const index = this.trips.value.findIndex(
      (trip: ITrip) => trip.id === this.tripSelected!.id,
    );
    const tripDetail = await this.tripsService.changeVehicle(
      this.tripSelected!.id!,
      id,
    );
    this.trips.value[index] = tripDetail;
    if (this.selectedTrip) {
      this.selectedTrip = tripDetail;
      this.tripDetails = tripDetail;
    }
    this.changeVehicle.hide();
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.changeVehicle',
      NotificationTypes.SUCCESS,
    );
  }

  async editCommentAction(comment: string) {
    const index = this.trips.value.findIndex(
      (trip: ITrip) => trip.id === this.tripSelected!.id,
    );
    const tripDetail = await this.tripsService.updateComment(
      this.tripSelected!.id!,
      comment ?? null,
    );
    if (index !== -1) this.trips.value[index] = tripDetail;
    if (this.selectedTrip) {
      this.selectedTrip = tripDetail;
      this.tripDetails = tripDetail;
    }
    this.editComment.hide();
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.editComment',
      NotificationTypes.SUCCESS,
    );
  }

  async addPaxAction(nPax: any) {
    if (!this.selectedTrip) return;
    this.tripDetails = await this.tripsService.addPax(
      this.selectedTrip.id || 0,
      nPax,
    );
    this.addPax.toggle();
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.addPax',
      NotificationTypes.SUCCESS,
    );
  }

  async setReadyAction() {
    const tripDetail = await this.tripsService.setStatus(
      this.tripSelected!.id!,
      ExpeditionStatus.READY,
    );
    await this.getTrips();
    const index = this.trips.value.findIndex(
      (trip: ITrip) => trip.id === this.tripSelected!.id,
    );
    if (index && index != -1) {
      this.trips.value[index] = tripDetail;
      if (this.selectedTrip) {
        this.selectedTrip = tripDetail;
        this.tripDetails = tripDetail;
      }
    }
    this.setReady.hide();
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.setReady',
      NotificationTypes.SUCCESS,
    );
  }

  async setClosedAction() {
    this.tripsService
      .setStatus(this.tripSelected!.id!, ExpeditionStatus.CLOSED)
      .then(async () => {
        await this.getTrips();
        if (this.selectedTrip) this.clearMap();
        this.utilsService.showNotification(
          images.notifications.check,
          'trips.actions.setClosed',
          NotificationTypes.SUCCESS,
        );
      })
      .catch((error: any) => {
        if (error.error_code === ErrorsCode.PERMISSION_DENIED) {
          this.notificationService.image = images.notification.error;
          this.notificationService.title = 'errorOccurred';
          this.notificationService.message = `${error.error_code}`;
          this.notificationService.translate = true;
          this.notificationService.show(NotificationTypes.DANGER);
        }
      })
      .finally(() => {
        this.setClosed.hide();
      });
  }

  async deleteTripAction(data: any) {
    this.deletingTrip = true;
    this.tripsService
      .deleteTrip(this.tripSelected!.id!, data)
      .then(async (resp: any) => {
        await this.getTrips();
        if (this.selectedTrip) this.clearMap();
        this.deleteTrip.hide();
        this.utilsService.showNotification(
          images.notifications.check,
          'trips.actions.deleteTrip',
          NotificationTypes.SUCCESS,
        );
      })
      .finally(() => {
        this.deletingTrip = false;
      });
  }

  async validatePickUpAction(bookingId: any) {
    const bookingIndex = this.bookings.findIndex(
      (booking: IBooking) => bookingId === booking.id,
    )!;
    const booking = await this.tripsService.validatePickUp(bookingId);
    this.bookings[bookingIndex] = booking;
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.validate',
      NotificationTypes.SUCCESS,
    );
  }

  async validateDropOffAction(bookingId: any) {
    const bookingIndex = this.bookings.findIndex(
      (booking: IBooking) => bookingId === booking.id,
    )!;
    const booking = await this.tripsService.validateDropOff(bookingId);
    this.bookings[bookingIndex] = booking;
    this.utilsService.showNotification(
      images.notifications.check,
      'trips.actions.validate',
      NotificationTypes.SUCCESS,
    );
  }

  async showChangeDriver(tripId: number) {
    if (!this.tripSelected) {
      const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
      this.tripSelected = trip!;
    }
    this.driverSelected = this.tripSelected?.driver.id || undefined;
    this.changeDriver = new Modal(document.getElementById('changeDriver'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.changeDriverShowed = false;
        this.driverSelected = undefined;
        this.tripSelected = undefined;
      },
      onShow: () => (this.changeDriverShowed = true),
    });
    this.changeDriver.show();
  }

  assignedVehicleOption() {
    const vehicle = this.tripSelected?.vehicle;
    return vehicle?.id
      ? [{ value: vehicle.id, label: vehicle.name ?? '' }]
      : [];
  }

  assignedDriverOption() {
    const driver = this.tripSelected?.driver;
    return driver?.id
      ? [
          {
            value: driver.id,
            label: `${driver.firstName ?? ''} ${driver.lastName ?? ''}`.trim(),
          },
        ]
      : [];
  }

  async showChangeVehicle(tripId: number) {
    if (!this.tripSelected) {
      const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
      this.tripSelected = trip!;
    }
    this.vehicleSelected = this.tripSelected?.vehicle.id || undefined;
    this.changeVehicle = new Modal(document.getElementById('changeVehicle'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.changeVehicleShowed = false;
        this.vehicleSelected = undefined;
        this.tripSelected = undefined;
      },
      onShow: () => (this.changeVehicleShowed = true),
    });
    this.changeVehicle.show();
  }

  async showEditComment(tripId: number) {
    if (!this.tripSelected) {
      const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
      this.tripSelected = trip!;
    }
    const tripDetail = await this.tripsService.getTrip(tripId);
    this.commentText = tripDetail.comment ?? '';
    this.editComment = new Modal(document.getElementById('editComment'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.editCommentShowed = false;
        this.commentText = undefined;
        this.tripSelected = undefined;
      },
      onShow: () => (this.editCommentShowed = true),
    });
    this.editComment.show();
  }

  async showAddPax(tripId: number) {
    // const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
    this.addPax = new Modal(document.getElementById('addPax'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.addPaxShowed = false),
      onShow: () => (this.addPaxShowed = true),
    });
    this.addPax.toggle();
  }

  async showSetReady(tripId: number) {
    if (!this.tripSelected) {
      const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
      this.tripSelected = trip!;
    }
    this.setReady = new Modal(document.getElementById('setReady'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.setReadyShowed = false;
        this.tripSelected = undefined;
      },
      onShow: () => (this.setReadyShowed = true),
    });
    this.setReady.toggle();
  }

  showSetClosed(tripId: number) {
    if (!this.tripSelected) {
      const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
      this.tripSelected = trip!;
    }
    this.setClosed = new Modal(document.getElementById('setClosed'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.setClosedShowed = false;
        this.tripSelected = undefined;
      },
      onShow: () => (this.setClosedShowed = true),
    });
    this.setClosed.toggle();
  }

  async showDeleteTrip(tripId: number) {
    if (!this.tripSelected) {
      const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
      this.tripSelected = trip!;
    }
    const bookings = await this.tripsService.getBookingsByTrip(tripId);
    this.bookings = await this.getRelatedBookings(bookings);
    this.deleteTrip = new Modal(document.getElementById('deleteTrip'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        this.deleteTripShowed = false;
        this.reason = '';
        this.tripSelected = undefined;
      },
      onShow: () => (this.deleteTripShowed = true),
    });
    this.deleteTrip.toggle();
  }

  async getRelatedBookings(bookings: IBookingDetails[]) {
    await Promise.all(
      bookings.map(async (booking) => {
        let relatedBookings: any[] = [];
        await Promise.all(
          booking.relatedBookings.map(async (bookingId: number) => {
            await this.bookingsService
              .getBooking(bookingId)
              .then((bookingDetail: any) => {
                relatedBookings.push(bookingDetail);
              });
          }),
        );
        booking.relatedBookingsInfo = relatedBookings;
      }),
    );
    return bookings;
  }

  private initViewBookingsModal() {
    this.viewBookings = new Modal(document.getElementById('viewBookings'), {
      placement: 'center',
      closable: false,
      onHide: () => {
        clearInterval(this.getBookingsInterval);
        this.viewBookingsShowed = false;
        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (this.tripDetails) {
          this.router.navigate([`${this.basePath}/${this.tripDetails.id}`], {
            replaceUrl: true,
            queryParams,
          });
        } else {
          this.router.navigate([this.basePath], { replaceUrl: true, queryParams });
        }
      },
      onShow: () => (this.viewBookingsShowed = true),
    });
  }

  async showViewBookings(tripId: number) {
    const trip = this.trips.value.find((trip: ITrip) => trip.id === tripId);
    this.tripSelected = trip || (this.tripDetails as ITrip);
    this.bookings = await this.tripsService.getBookingsByTrip(tripId);
    this.getBookingsInterval = setInterval(async () => {
      this.bookings = await this.tripsService.getBookingsByTrip(tripId);
    }, 10000);
    this.initViewBookingsModal();
    this.viewBookings.toggle();
  }

  async showViewPickUps(stopId: number) {
    this.bookings = await this.tripsService.getBookingsByStop(
      this.tripDetails!.id!,
      stopId,
      true,
    );
    this.getBookingsInterval = setInterval(async () => {
      this.bookings = await this.tripsService.getBookingsByStop(
        this.tripDetails!.id!,
        stopId,
        true,
      );
    }, 10000);
    this.initViewBookingsModal();
    this.viewBookings.toggle();
  }

  async showViewDropOffs(stopId: number) {
    this.bookings = await this.tripsService.getBookingsByStop(
      this.tripDetails!.id!,
      stopId,
      false,
    );
    this.getBookingsInterval = setInterval(async () => {
      this.bookings = await this.tripsService.getBookingsByStop(
        this.tripDetails!.id!,
        stopId,
        false,
      );
    }, 10000);
    this.initViewBookingsModal();
    this.viewBookings.toggle();
  }

  async showViewChangeOvers(stopId: number) {
    this.bookings = await this.tripsService.getChangeoversByStop(stopId);
    this.getBookingsInterval = setInterval(async () => {
      this.bookings = await this.tripsService.getChangeoversByStop(stopId);
    }, 10000);
    this.initViewBookingsModal();
    this.viewBookings.toggle();
  }

  prepareToDeleteBooking(bookingId: any) {
    this.bookingToDelete = bookingId;
    this.deleteBooking = new Modal(
      document.getElementById('deleteBookingFromTrips'),
      {
        placement: 'center',
        closable: false,
        onHide: () => {
          this.deleteBookingShowed = false;
          this.comment = '';
        },
        onShow: () => (this.deleteBookingShowed = true),
      },
    );
    this.deleteBooking.toggle();
  }

  async deleteBookingAction(data: any) {
    this.deletingBooking = true;
    await this.bookingsService
      .deleteBooking(
        this.bookingToDelete,
        this.comment,
        data?.sendNotification,
        data?.refund,
        data?.fullRefund,
        data?.related,
      )
      .then(
        () => {
          this.utilsService.showNotification(
            images.notifications.check,
            'bookings.actions.deleteBooking',
            NotificationTypes.SUCCESS,
          );
        },
        () => {},
      );
    this.deleteBooking.toggle();
    this.viewBookings.toggle();
    this.deletingBooking = false;
  }

  getDrivenRoute() {
    this.tripsService
      .getDrivenRoute(this.selectedTrip!.id!)
      .then((res: any) => {
        this.mapUtilsService.addPositionsClustersAndRoutes(this.map, res);
      });
  }

  setDrivenRouteInterval() {
    if (!this.selectedTrip) return;
    this.getDrivenRoute();
    this.getDrivenRouteInterval = setInterval(() => {
      this.getDrivenRoute();
    }, 10000);
  }

  showDrivenRoute(show: boolean) {
    this.mapUtilsService.setStopCirclesVisibility(show ? this.map : null);
    if (show) {
      this.setDrivenRouteInterval();
    } else {
      this.mapUtilsService.clearPositionsClusterAndRoutes();
      clearInterval(this.getDrivenRouteInterval);
    }
  }

  clearIntervals() {
    clearInterval(this.getTripInterval);
    clearInterval(this.getBookingsInterval);
    clearInterval(this.getVehiclesInterval);
    clearInterval(this.getDrivenRouteInterval);
    clearInterval(this.positionsRefreshInterval);
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    this.controller.abort();
    this.clearIntervals();
    this.routerSubscription?.unsubscribe();
    this.navigationSubscription?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    this.storageService.setTitleKey(null);
    this.listExportService.clear(ExportableListType.TRIPS);
  }
}
